export type Locale = 'en' | 'es';

type Dictionary = Record<string, string>;

const en: Dictionary = {
  'app.brand': 'MockForge',
  'app.home': 'Home',
  'app.refresh': 'Refresh',
  'app.loading': 'Loading...',
  'app.pageNotFoundTitle': 'Page Not Found',
  'app.pageNotFoundBody': "The page you're looking for doesn't exist yet.",
  'app.goToDashboard': 'Go to Dashboard',
  'app.searchPlaceholder': 'Search pages and logs…',
  'a11y.skipNavigation': 'Skip to navigation',
  'a11y.skipMain': 'Skip to main content',
  'a11y.skipSearch': 'Skip to search',
  'a11y.mainNavigation': 'Main navigation',
  'a11y.mainContent': 'Main content',
  'a11y.collapseSidebar': 'Collapse sidebar',
  'a11y.expandSidebar': 'Expand sidebar',
  'a11y.searchScope': 'Search scope',
  'search.scope.all': 'All',
  'search.scope.current': 'Current page',
  'search.scope.logs': 'Logs only',
  'search.scope.services': 'Services only',
  'workspace.selector.label': 'Active workspace',
  'workspace.selector.placeholder': 'Select workspace…',

  'nav.core': 'Core',
  'nav.servicesData': 'Services & Data',
  'nav.protocolBrokers': 'Protocol Brokers',
  'nav.orchestration': 'Orchestration',
  'nav.observability': 'Observability',
  'nav.testing': 'Testing',
  'nav.chaosResilience': 'Chaos & Resilience',
  'nav.importTemplates': 'Import & Templates',
  'nav.aiIntelligence': 'AI & Intelligence',
  'nav.community': 'Community',
  'nav.plugins': 'Plugins',
  'nav.configuration': 'Configuration',
  'nav.help': 'Help',

  'tab.dashboard': 'Dashboard',
  'tab.workspaces': 'Workspaces',
  'tab.federation': 'Federation',
  'tab.services': 'Services',
  'tab.virtualBackends': 'Virtual Backends',
  'tab.fixtures': 'Fixtures',
  'tab.overrides': 'Overrides',
  'tab.hostedMocks': 'Hosted Mocks',
  'tab.apiExplorer': 'API Explorer',
  'tab.tunnels': 'Tunnels',
  'tab.proxyInspector': 'Proxy Inspector',
  'tab.smtpMailbox': 'SMTP Mailbox',
  'tab.mqttBroker': 'MQTT Broker',
  'tab.kafkaBroker': 'Kafka Broker',
  'tab.amqpBroker': 'AMQP Broker',
  'tab.faq': 'FAQ',
  'tab.support': 'Support',
  'tab.chains': 'Chains',
  'tab.graph': 'Graph',
  'tab.stateMachines': 'State Machines',
  'tab.scenarioStudio': 'Scenario Studio',
  'tab.orchestrationBuilder': 'Orchestration Builder',
  'tab.orchestrationExecution': 'Orchestration Execution',
  'tab.observability': 'Observability',
  'tab.worldState': 'World State',
  'tab.performance': 'Performance',
  'tab.systemStatus': 'System Status',
  'tab.incidents': 'Incidents',
  'tab.logs': 'Logs',
  'tab.traces': 'Traces',
  'tab.metrics': 'Metrics',
  'tab.analytics': 'Analytics',
  'tab.pillarAnalytics': 'Pillar Analytics',
  'tab.fitnessFunctions': 'Fitness Functions',
  'tab.verification': 'Verification',
  'tab.contractDiff': 'Contract Diff',
  'tab.testing': 'Testing',
  'tab.testGenerator': 'Test Generator',
  'tab.testExecution': 'Test Execution',
  'tab.integrationTests': 'Integration Tests',
  'tab.conformance': 'Conformance',
  'tab.timeTravel': 'Time Travel',
  'tab.chaosEngineering': 'Chaos Engineering',
  'tab.resilience': 'Resilience',
  'tab.recorder': 'Recorder',
  'tab.behavioralCloning': 'Behavioral Cloning',
  'tab.import': 'Import',
  'tab.templateMarketplace': 'Template Marketplace',
  'tab.scenarioMarketplace': 'Scenario Marketplace',
  'tab.aiStudio': 'AI Studio',
  'tab.mockai': 'MockAI',
  'tab.mockaiOpenApiGenerator': 'MockAI OpenAPI Generator',
  'tab.mockaiRules': 'MockAI Rules',
  'tab.voiceLlm': 'Voice + LLM',
  'tab.showcase': 'Showcase',
  'tab.learningHub': 'Learning Hub',
  'tab.plugins': 'Plugins',
  'tab.cloudPlugins': 'Cloud Plugins',
  'tab.pluginRegistry': 'Plugin Registry',
  'tab.config': 'Config',
  'tab.organization': 'Organization',
  'tab.billing': 'Billing',
  'tab.apiTokens': 'API Tokens',
  'tab.publisherKeys': 'Publisher Keys',
  'tab.byok': 'BYOK Keys',
  'tab.usage': 'Plan & Usage',
  'tab.userManagement': 'User Management',
  'tab.notificationChannels': 'Notification Channels',
  'tab.cloudSnapshots': 'Snapshots',
  'tab.cloudIncidents': 'Cloud Incidents',
  'tab.cloudTestRuns': 'Cloud Test Runs',
  'tab.cloudTraces': 'Cloud Traces',
  'tab.cloudChaos': 'Cloud Chaos',
  'tab.cloudFlows': 'Cloud Flows',
  'tab.cloudContract': 'Cloud Contract',
  'tab.cloudRecorder': 'Cloud Recorder',
  'tab.cloudBehavioralCloning': 'Cloud Behavioral Cloning',
  'tab.cloudShowcaseAdmin': 'Showcase Admin',

  'nav.localOnly.badge': 'Local',
  'nav.localOnly.tooltip': 'Requires a local MockForge instance',
  'nav.mocks': 'Mocks',
  'nav.protocols': 'Protocols',
  'nav.flows': 'Flows',
  'nav.observe': 'Observe',
  'nav.test': 'Test & Verify',
  'nav.resilience': 'Resilience',
  'nav.ai': 'AI',
  'nav.ecosystem': 'Ecosystem',
  'nav.settings': 'Settings',
  'nav.localOnly.section': 'Local only',
  'nav.localOnly.hint': 'These pages need a self-hosted MockForge instance.',
  'tab.contracts': 'Contracts',
  'tab.testRuns': 'Test Runs',
  'tab.savedFlows': 'Saved Flows',
  'shell.search': 'Search',
  'shell.searchPlaceholder': 'Search pages, workspaces and actions…',
  'shell.noResults': 'No results for',
  'shell.group.recent': 'Recent',
  'shell.group.pages': 'Pages',
  'shell.group.workspaces': 'Workspaces',
  'shell.group.actions': 'Actions',
  'shell.action.searchLogs': 'Search logs for',
  'shell.action.searchServices': 'Search services for',
  'shell.action.toggleTheme': 'Toggle light / dark theme',
  'shell.action.refresh': 'Refresh data',
  'shell.action.toggleSidebar': 'Collapse / expand sidebar',
  'shell.action.help': 'Help & keyboard shortcuts',
  'shell.workspace.none': 'No workspace',
  'shell.workspace.manage': 'Manage workspaces',
  'shell.workspace.switch': 'Switch to',
  'shell.openMenu': 'Open navigation',
  'shell.closeMenu': 'Close navigation',
  'shell.help': 'Help & support',
  'shell.docs': 'Documentation',
  'user.account': 'Account settings',
  'user.profile': 'Profile',
  'user.preferences': 'Preferences',
  'user.language': 'Language',
  'user.signOut': 'Sign out',

  'page.config.title': 'Configuration',
  'page.config.subtitle': 'Manage MockForge settings and preferences',
  'page.plugins.title': 'Plugin Management',
  'page.plugins.subtitle': 'Manage authentication, template, response, and datasource plugins',
  'page.plugins.error': 'Error',
  'page.plugins.marketplaceTitle': 'Plugin Marketplace',
  'page.plugins.marketplaceBody': 'Browse and install plugins from the official marketplace',
  'page.plugins.browseMarketplace': 'Browse Marketplace',
  'page.plugins.installPlugin': 'Install Plugin',
  'page.plugins.reloadAll': 'Reload All',
  'page.cloudPlugins.title': 'Cloud Plugins',
  'page.cloudPlugins.subtitle':
    'Manage plugins attached to your hosted-mock deployments. Each attachment has its own permission grants and runtime config.',
  'page.cloudPlugins.browseRegistry': 'Browse Registry',
  'page.mockai.title': 'MockAI',
  'page.mockai.description':
    'AI-powered mock API intelligence for realistic, context-aware responses',
  'page.mockai.quickActions': 'Quick Actions',
  'page.mockai.features': 'Features',
};

const es: Dictionary = {
  'app.brand': 'MockForge',
  'app.home': 'Inicio',
  'app.refresh': 'Actualizar',
  'app.loading': 'Cargando...',
  'app.pageNotFoundTitle': 'Pagina no encontrada',
  'app.pageNotFoundBody': 'La pagina que buscas aun no existe.',
  'app.goToDashboard': 'Ir al panel',
  'app.searchPlaceholder': 'Buscar paginas y registros…',
  'a11y.skipNavigation': 'Saltar a navegacion',
  'a11y.skipMain': 'Saltar al contenido principal',
  'a11y.skipSearch': 'Saltar a busqueda',
  'a11y.mainNavigation': 'Navegacion principal',
  'a11y.mainContent': 'Contenido principal',
  'a11y.collapseSidebar': 'Contraer barra lateral',
  'a11y.expandSidebar': 'Expandir barra lateral',
  'a11y.searchScope': 'Ambito de busqueda',
  'search.scope.all': 'Todo',
  'search.scope.current': 'Pagina actual',
  'search.scope.logs': 'Solo registros',
  'search.scope.services': 'Solo servicios',
  'workspace.selector.label': 'Espacio de trabajo activo',
  'workspace.selector.placeholder': 'Seleccionar espacio…',

  'nav.core': 'Nucleo',
  'nav.servicesData': 'Servicios y Datos',
  'nav.protocolBrokers': 'Brokers de Protocolo',
  'nav.orchestration': 'Orquestacion',
  'nav.observability': 'Observabilidad',
  'nav.testing': 'Pruebas',
  'nav.chaosResilience': 'Caos y Resiliencia',
  'nav.importTemplates': 'Importar y Plantillas',
  'nav.aiIntelligence': 'IA e Inteligencia',
  'nav.community': 'Comunidad',
  'nav.plugins': 'Plugins',
  'nav.configuration': 'Configuracion',
  'nav.help': 'Ayuda',

  'nav.localOnly.badge': 'Local',
  'nav.localOnly.tooltip': 'Requiere una instancia local de MockForge',
  'nav.mocks': 'Mocks',
  'nav.protocols': 'Protocolos',
  'nav.flows': 'Flujos',
  'nav.observe': 'Observar',
  'nav.test': 'Pruebas y verificacion',
  'nav.resilience': 'Resiliencia',
  'nav.ai': 'IA',
  'nav.ecosystem': 'Ecosistema',
  'nav.settings': 'Ajustes',
  'nav.localOnly.section': 'Solo local',
  'nav.localOnly.hint': 'Estas paginas requieren una instancia local de MockForge.',
  'tab.contracts': 'Contratos',
  'tab.testRuns': 'Ejecuciones de pruebas',
  'tab.savedFlows': 'Flujos guardados',
  'shell.search': 'Buscar',
  'shell.searchPlaceholder': 'Buscar paginas, espacios y acciones…',
  'shell.noResults': 'Sin resultados para',
  'shell.group.recent': 'Recientes',
  'shell.group.pages': 'Paginas',
  'shell.group.workspaces': 'Espacios de trabajo',
  'shell.group.actions': 'Acciones',
  'shell.action.searchLogs': 'Buscar en registros',
  'shell.action.searchServices': 'Buscar en servicios',
  'shell.action.toggleTheme': 'Cambiar modo claro / oscuro',
  'shell.action.refresh': 'Actualizar datos',
  'shell.action.toggleSidebar': 'Contraer / expandir barra lateral',
  'shell.action.help': 'Ayuda y atajos de teclado',
  'shell.workspace.none': 'Sin espacio de trabajo',
  'shell.workspace.manage': 'Gestionar espacios',
  'shell.workspace.switch': 'Cambiar a',
  'shell.openMenu': 'Abrir navegacion',
  'shell.closeMenu': 'Cerrar navegacion',
  'shell.help': 'Ayuda y soporte',
  'shell.docs': 'Documentacion',
  'user.account': 'Configuracion de cuenta',
  'user.profile': 'Perfil',
  'user.preferences': 'Preferencias',
  'user.language': 'Idioma',
  'user.signOut': 'Cerrar sesion',
};

export const translations: Record<Locale, Dictionary> = { en, es };
